/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { formatPtlDate } from '@app/app/shared/pipes/ptl-date.pipe';
import { LucideCalendar, LucideClock } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { V3ContentCardMetaEntry, V3ContentCardStatusBadge } from './v3-content-card.model';

export const V3_CARD_DUE_SOON_DAYS = 7;

export type V3CardDeadlineState = 'upcoming' | 'dueSoon' | 'overdue';

export interface V3ContentCardScheduleInput {
  deadline?: Date | string | null;
  eventStart?: string | null;
  completed?: boolean;
  now?: Date;
}

export interface V3ContentCardSchedule {
  /** The dated chip — pinned to the cover in grid view, folded into the meta row in list view. */
  coverBadge: V3ContentCardMetaEntry | null;
  badge: V3ContentCardStatusBadge | null;
}

const MS_PER_DAY = 86_400_000;

export function v3CardDeadlineState(value: Date | string | null | undefined, now: Date = new Date()): V3CardDeadlineState | null {
  const date = toDate(value);
  if (!date) {
    return null;
  }
  if (date.getTime() < now.getTime()) {
    return 'overdue';
  }
  const startOfToday = startOfDay(now);
  const startOfTarget = startOfDay(date);
  const days = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / MS_PER_DAY);
  return days <= V3_CARD_DUE_SOON_DAYS ? 'dueSoon' : 'upcoming';
}

export function v3ContentCardSchedule(input: V3ContentCardScheduleInput, transloco: TranslocoService): V3ContentCardSchedule {
  const eventStart = toDate(input.eventStart);
  if (eventStart) {
    return {
      coverBadge: {
        icon: LucideCalendar,
        text: transloco.translate('translations.cardItem.schedule.event', { date: v3CardScheduleDate(eventStart, transloco) }),
      },
      badge: null,
    };
  }

  const deadline = toDate(input.deadline);
  if (!deadline || input.completed) {
    return { coverBadge: null, badge: null };
  }

  return {
    coverBadge: {
      icon: LucideClock,
      text: transloco.translate('translations.cardItem.schedule.due', { date: v3CardScheduleDate(deadline, transloco) }),
    },
    badge: deadlineBadge(v3CardDeadlineState(deadline, input.now ?? new Date()), transloco),
  };
}

function deadlineBadge(state: V3CardDeadlineState | null, transloco: TranslocoService): V3ContentCardStatusBadge | null {
  if (state === 'overdue') {
    return { label: transloco.translate('translations.cardItem.schedule.overdue'), tone: 'danger' };
  }
  if (state === 'dueSoon') {
    return { label: transloco.translate('translations.cardItem.schedule.dueSoon'), tone: 'warning' };
  }
  return null;
}

// A deadline stored at exactly midnight carries no meaningful time-of-day, so it renders as a plain date.
export function v3CardScheduleDate(date: Date, transloco: TranslocoService): string {
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
  return formatPtlDate(date, hasTime ? 'shortDateTime' : 'date', transloco.getActiveLang());
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
